/**
 * The browser `sessionStorage` item an E2E run writes the PayPro test-mode key
 * into, and the subscribe page reads it back from to send as
 * `StoreGetProductsParams.payProTestModeKey` (df-59d822c22018). Session storage
 * rather than the address: anything in the address can end up in analytics as
 * the page URL. Only the E2E runner ever writes this item.
 */
export const PAYPRO_TEST_MODE_KEY_SESSION_ITEM = 'cs-e2e-paypro-test-mode-key';

export interface StoreGetProductsParams {
  /// domain from which the request is coming (to be able to distinguish between cf and mhp, for example)
  domain: string;
  /// 2-letter country code
  country: string;
  // any additional telemetry that we want to keep tracking in base64encoded JSON
  // (json tagged as x-data, we don't want to scream "telemetry" in URLs)
  telemetryData: string;
  /**
   * The resolved `WebPaymentProvider` mode string the client read from the
   * config DB (`ConfigKey.WebPaymentProvider`). The `storeGetCheckout`
   * dispatcher passes it to `selectWebProvider` to decide PayPro vs Paddle.
   * Optional: the legacy `storeGetProducts_PayPro` callable ignores it, and a
   * missing value is treated as `'payProOnly'` (the safe default). The mode only
   * influences which rail a *prod* user gets; the webhook grant-gate is the
   * authoritative money gate, so a tampered mode can never grant a free license.
   * See design-docs/paddle-integration.md decision 1.
   */
  mode?: string;
  /**
   * Override that forces the web rail (`'payPro'` | `'paddle'`) regardless of
   * the rollout rules, set by the client from the `forceWebProvider` URL query
   * param. It lets a test (E2E or manual) exercise either checkout without
   * flipping the global `WebPaymentProvider` config — which would change the
   * rail for every user and break unrelated CI runs. The server applies it via
   * `resolveForcedProvider`, in every environment for anyone. It only picks the
   * rail; the webhook grant-gate remains the authoritative money gate, so it can
   * never grant a free license. Any other / missing value is ignored.
   */
  forceProvider?: string;
  /**
   * A stable per-browser anonymous id the client mints once and reuses
   * (`localStorage` key `cs-paddle-bucket-id`), used to bucket a **logged-out**
   * purchase-first buyer for the Paddle rollout ramp (#3910). The dispatcher
   * uses it as the bucketing identifier only when there is no logged-in identity
   * — a logged-in request buckets on `normalizeEmail(email) || uid` instead. It
   * affects only which rail builds the checkout; the webhook grant-gate stays the
   * authoritative money gate. See design-docs/paddle-integration.md ("Phase 2 — Paddle for new users in production (the percentile ramp)").
   */
  bucketingId?: string;
  /**
   * The UI language the app is currently showing (the client's `BOOT_LOCALE`),
   * sent so the PayProGlobal checkout URL can carry it as an `x-locale` custom
   * field (#4244). The IPN reads it back and hands it to the welcome email,
   * which is sent for an account so new that nothing is saved in its settings
   * yet — so this is the only place the app's language and the purchase meet.
   * Optional: a missing value simply leaves the email in English.
   */
  uiLanguage?: string;
  /**
   * The PayPro test-mode key, presented by an E2E run. The server builds a
   * test-mode PayPro checkout only when this matches its own key — see
   * `isPayProTestModeRequest`. An ordinary browser never sends it. The dispatch
   * log redacts it by name (`REDACT_LOG_FIELDS`).
   */
  payProTestModeKey?: string;
}

/**
 * The Paddle inline-checkout config the `storeGetCheckout` dispatcher returns
 * when `selectWebProvider` picks Paddle. The browser inits Paddle.js with
 * `clientToken` (a publishable token, not a secret) and opens an inline checkout
 * for the chosen price. `environment` is resolved server-side per decision 3
 * (prod + non-e2e → live; otherwise sandbox). `isE2e` flows into
 * `custom_data.is_e2e`, which the webhook sandbox grant-gate reads.
 */
export interface PaddleCheckoutConfig {
  provider: 'paddle';
  environment: 'sandbox' | 'live';
  clientToken: string;
  priceMonthly: string;
  priceYearly: string;
  /** The coach per-seat coaching price id (Phase 4, #3919). The become-a-coach
   *  web flow opens a Paddle checkout for THIS price at `quantity = 1` (the
   *  coach's own seat). It rides on the same config the personal checkout uses
   *  so the one `buildPaddleCheckout` builder serves both flows; the personal
   *  subscribe page simply ignores it. See design-docs/paddle-integration.md
   *  "Phase 4 — Coach per-seat coaching subscription". */
  priceCoachSeat: string;
  /** A unique id we generate per checkout; stamped into `custom_data.checkout_id`. */
  checkoutId: string;
  /**
   * True when this checkout was built with the referral prices, so the friend really
   * is getting their free month (#4962 phase 2). The subscribe page renders its
   * "your free month is applied" line from THIS, and never recomputes entitlement
   * itself — the server decides, because only the server can see the referral record
   * and resolve the inviter's flag.
   *
   * OPTIONAL, and that is load-bearing rather than tidiness.
   * `CoachStartPaddleCheckoutResult` is `{ error: string } | PaddleCheckoutConfig`,
   * and `coachStartPaddleCheckout` builds its config through the deliberately
   * referral-unaware synchronous builder — so a REQUIRED field here would stop the
   * coach path compiling, breaking the "coach path unchanged" promise with a type.
   * Absent means "not entitled", which is also the right answer for a coach seat.
   */
  referralFreeMonthApplied?: boolean;
  /**
   * Whether the prices in this checkout carry a free trial (#6552). False for a
   * returning customer who already used a trial on any rail: they are handed the
   * no-trial prices and pay on the day they subscribe. The subscribe page must read
   * this, never guess, before it promises a trial (#6145).
   *
   * OPTIONAL for the same reason as `referralFreeMonthApplied`: the coach-seat
   * checkout builds through the same builder and leaves it absent. Every personal
   * checkout sets it.
   */
  trialIncluded?: boolean;
  /**
   * True when the subscribe page should offer the "Were you invited? Enter your
   * friend's code" box (#4962 phase 2) — the way back in for somebody whose invite
   * link did not carry the referral through.
   *
   * Gated on the referral flag's GLOBAL value AND on the caller having no referral
   * record, and BOTH halves matter. The viewer's own resolved flag would be the
   * obvious gate and is wrong: it would hide the box from every referred friend for
   * the whole per-inviter rollout, which is precisely the population it exists for.
   * And showing it to somebody who already HAS a referrer only invites a second code
   * that is certain to be refused.
   *
   * This field appears on BOTH branches of `StoreGetProductsResults`, not only here.
   * A signed-in user with no referral is not a referred friend, so the routing
   * override does not engage and under `payProOnly` or a low ramp they are returned
   * the PayPro branch — put the gate on this branch alone and the box is absent for
   * exactly the people it is promised to.
   */
  offerReferralCodeEntry?: boolean;
  /** True when the request carried the `e2e-test` marker. */
  isE2e: boolean;
  /** The caller's existing Paddle customer id (`ctm_…`), when they already are a
   *  Paddle customer (e.g. a coach who is already a personal Paddle subscriber).
   *  The client opens the checkout with `customer: { id }` instead of an email so
   *  the new coaching subscription attaches to the buyer's existing Paddle
   *  customer record (no duplicate customer is created). This is recognition
   *  ONLY — on its own it does NOT skip the "enter your details" page and does NOT
   *  offer a saved card. Skipping the details page also needs the saved address id
   *  ({@link existingAddressId}); offering a saved card needs a {@link customerAuthToken}.
   *  Absent for a first-time Paddle buyer (they enter details fresh). Set by the
   *  become-a-coach gate (Phase 4); the personal subscribe flow ignores it. */
  existingCustomerId?: string;
  /** The caller's saved Paddle billing-address id (`add_…`), captured from a
   *  prior webhook (#4097). Passed at checkout as `customer.address.id` alongside
   *  {@link existingCustomerId} so Paddle skips the "enter your details" page for
   *  this returning customer. Absent for a first-time buyer, or when we have not
   *  yet captured an address for them. Used only as the fallback path when no
   *  {@link customerAuthToken} is available (the token already presents the saved
   *  address). Set by the become-a-coach gate; the personal subscribe flow ignores it. */
  existingAddressId?: string;
  /** A short-lived Paddle `customerAuthToken` minted server-side at checkout-start
   *  for a returning customer (#4097). When present, the client opens the checkout
   *  with `customerAuthToken` INSTEAD of `customer` (Paddle.js makes the two
   *  mutually exclusive): the token authenticates the returning customer, so Paddle
   *  presents their saved address (skips the details page) AND their saved card
   *  (when they opted in on a prior purchase — best-effort UX, not guaranteed).
   *
   *  This is a CREDENTIAL: it is fetched at point of use, never persisted, and
   *  never logged. (Like the purchase claim token, the value does still pass
   *  through the access-controlled onCall dispatcher log — the same accepted caveat
   *  documented in design-docs/paddle-integration.md decision 7 / A1.) Absent when
   *  the caller is a first-time buyer or when token minting failed (the checkout
   *  then falls back to {@link existingAddressId}). Set by the become-a-coach gate. */
  customerAuthToken?: string;
  /** The buyer's real public IP address, as this server saw it on the
   *  checkout-build request (#4045).
   *
   *  A Paddle webhook arrives from Paddle's servers, so it carries no buyer IP —
   *  which is why the Paddle conversion pixels used to send an empty one and why
   *  Paddle telemetry could not be geo-located, both unlike PayPro (whose IPN
   *  carries `CUSTOMER_IP`). But THIS call comes straight from the buyer's own
   *  browser, so the address is available right here. We hand it back so the page
   *  can stamp it into the checkout's `custom_data` and the webhook can use it for
   *  Mixpanel geo and as a Meta match key.
   *
   *  It is used for ad matching and analytics geo ONLY — never for authentication,
   *  entitlement, or any other gate — so a tampered value cannot buy anything. */
  clientIp?: string;
  /** True when this server saw `Sec-GPC: 1` on the checkout-build request
   *  (#5663) — the browser's Global Privacy Control signal, which several US
   *  state laws treat as the person opting out of having their data shared for
   *  advertising.
   *
   *  It is carried back because some browsers and extensions send only the
   *  header and never set the page-visible `navigator.globalPrivacyControl`
   *  property, so the page cannot see its own signal. The page ORs this with
   *  what it can see before it decides the Advertising answer this checkout
   *  carries.
   *
   *  Omitted rather than false when no signal was seen, so an older page and
   *  every existing caller behave exactly as before. */
  gpcDetected?: true;
}

export interface StoreGetProductsOneProduct {
  title: string;  // name shown to the user
  id: string;
  name: string; // internal name used in PayPro and shown in analytics
  numMonths: number;  // nmber of months in the subscription
  displayPrice?: number;  // these are not returned if PayPro fails for some reason
  displayCurrency?: string;  // these are not returned if PayPro fails for some reason
  buyUrl: string;
}

export type StoreGetProductsResults = {
  error: string;
} | {
  /** Present on the `storeGetCheckout` dispatcher's PayPro branch; omitted by
   *  the legacy `storeGetProducts_PayPro` callable (back-compat). */
  provider?: 'payPro';
  products: StoreGetProductsOneProduct[];
  /**
  This returns the number configured in firebase functions:config:get as paypro.productgeneration
  */
  productGeneration: number;  // matched by CouchUserInfo.payProData.productGeneration
  /** Whether to offer the referral code-entry box. See the field of the same name on
   *  {@link PaddleCheckoutConfig} for the gate and why it rides on BOTH branches. */
  offerReferralCodeEntry?: boolean;
} | PaddleCheckoutConfig
